import React, { useRef, useEffect, memo } from 'react'; 
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import useGeoData from '../../../store/useGeoData'; 
import type { GeoDataState } from '../../../store/types'; 
import { useMapInitialization } from '../hooks/useMapInitialization';
import { useMapControls } from '../hooks/useMapControls';
import { useBasemapSync } from '../hooks/useBasemapSync';
import { useMapLayers } from '../hooks/useMapLayers'; 
import { useMapZoomToLayer } from '../hooks/useMapZoomToLayer'; 
import { useLayerPropertyDiscovery } from '../hooks/useLayerPropertyDiscovery'; 
import { useContoursLayer } from '../../contours/hooks/useContoursLayer'; 
import { FixedFeaturePopup } from './FixedFeaturePopup'; 
import DrawControl from './DrawControl';
 

const MapView: React.FC = memo(() => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  // Seleziona stati e azioni necessari dallo store
  const {
    isTerrainActive,
    terrainSourceUrl,
    terrainExaggeration,
    isDrawWidgetActive,
  } = useStoreWithEqualityFn(
    useGeoData,
    (state: GeoDataState) => ({
      isTerrainActive: state.isTerrainActive,
      terrainSourceUrl: state.terrainSourceUrl,
      terrainExaggeration: state.terrainExaggeration,
      isDrawingEnabled: state.isEnabled, 
      setSelectedArea: state.setSelectedArea, 
      isDrawWidgetActive: state.isDrawWidgetActive, 
    }),
    shallow
  );

  // 1. Inizializzazione Mappa
  // Ottiene mapRef, isMapLoaded
  const {
    mapRef,
    isMapLoaded
  } = useMapInitialization(mapContainerRef);

  // 2. Sincronizzazione Basemap
  // Passa solo mapRef.current
  useBasemapSync({
    map: mapRef.current
  });

  // 3. Gestione Controlli Mappa
  // Passa solo mapRef.current e isMapLoaded
  useMapControls({
    map: mapRef.current,
    isMapLoaded
  });

  // 5. Gestione Layer GeoJSON (hook esistente)
  // Passa solo mapRef.current
  useMapLayers(mapRef.current);
 
  // 6. Gestione Zoom a Layer specifico (nuovo hook)
  // Passa solo mapRef.current
  useMapZoomToLayer(mapRef.current);

  // 7. Scoperta Proprietà Layer (nuovo hook)
  useLayerPropertyDiscovery(mapRef.current);

  // 8. Gestione Layer Curve di Livello (nuovo hook) - Rinumerato
  useContoursLayer(mapRef.current);

  // 9. Effetto per sincronizzare lo stato del Terrain 3D con la mappa
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return; // Assicurati che la mappa sia inizializzata

    const TERRAIN_SOURCE_ID = 'aws-terrain-dem'; // Usa un ID sorgente coerente

    try {
      if (isTerrainActive && terrainSourceUrl) {
        // Terrain Attivo
        const source = map.getSource(TERRAIN_SOURCE_ID);
        if (!source) {
          // Aggiungi la sorgente solo se non esiste
          map.addSource(TERRAIN_SOURCE_ID, {
            type: 'raster-dem',
            // Usa 'tiles' array invece di 'url'
            tiles: [terrainSourceUrl], // terrainSourceUrl viene dallo store
            tileSize: 256, // Modifica tileSize
            attribution: "Terrain data &copy; AWS Terrain Tiles", // Aggiungi attribution
            encoding: 'terrarium'
          });
          // console.log(`Terrain source '${TERRAIN_SOURCE_ID}' added.`);
        }
        // Imposta o aggiorna il terrain con l'esagerazione corrente
        map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: terrainExaggeration });
        // console.log(`Terrain set with source '${TERRAIN_SOURCE_ID}' and exaggeration ${terrainExaggeration}.`); // Già commentato

      } else {
        // Terrain Disattivo
        // Verifica se il terrain è attualmente impostato prima di rimuoverlo
        if (map.getTerrain()) {
           map.setTerrain(null);
           // console.log('Terrain removed.');
        }
      }
    } catch (error) {
       console.error("[MapView] Error updating map terrain:", error); // Mantenuto errore
       // Gestisci eventuali errori durante l'aggiornamento della mappa
    }

  }, [mapRef, isTerrainActive, terrainSourceUrl, terrainExaggeration]); // Dipendenze dell'effetto

  // Il componente ora renderizza solo il contenitore della mappa.
  // Tutta la logica complessa è delegata agli hook.
  return (
    // Aggiungiamo un wrapper con position: relative per permettere il posizionamento absolute del popup
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={mapContainerRef}
        style={{ width: '100%', height: '100%' }} // Usa 100% per adattarsi al contenitore padre
        // Aggiungiamo un ID per eventuali stili globali o test e2e se necessario
        id="map-container"
      />
      {/* Includi il componente popup fisso qui */}
      <FixedFeaturePopup />
      {/* Passa lo stato isDrawWidgetActive al DrawControl */}
      <DrawControl map={mapRef.current} isActive={isDrawWidgetActive} />
    </div>
  );
});

MapView.displayName = 'MapView';

export default MapView;
